import erGyldigKontonummer from '../../norsk-validator/erGyldigKontonummer';
import { notAsked, RemoteDataType } from '../../reducers/remoteData';
import {
    DateAsString,
    ForsikringDekningDTO,
    SporsmalType,
    SvarDTOUnion,
    TallValidationType,
    TextInputSize,
    UtleggType,
    Valideringstype,
    VerkstedWebDTO,
} from '../../types/generated-types';
import { Kundeinfo } from '../../types/internal-types';
import { assertUnreachable } from '../../utils/compiler-utils';
import {
    BesvartAttentionMessageSporsmal,
    CurrentAttentionMessageSporsmal,
} from '../attentionMessage/attention-message-types';
import { CurrentBlindveisporsmal } from '../blindvei/blindvei-types';
import { BesvartCardSporsmal, CurrentCardSporsmal } from '../card/card-types';
import {
    BesvartCheckboxSporsmal,
    CurrentCheckboxSporsmal,
    isBesvartCheckboxSporsmal,
} from '../checkbox/checkbox-types';
import { BesvartDatosporsmal, CurrentDatosporsmal, isBesvartDatosporsmal } from '../dato/dato-types';
import {
    BesvartFilopplastingsporsmal,
    CurrentFilopplastingsporsmal,
    isFilopplastingsporsmal,
    VedleggStatus,
} from '../filopplasting/filopplasting-types';
import {
    BesvartForsikringObjekterCheckboxSporsmal,
    CurrentForsikringObjekterCheckboxSporsmal,
    getValgteObjekterFromBesvartSporsmalOrEmpty,
    isBesvartForsikringObjekterCheckboxSporsmal,
    validateForsikringObjekterCheckbox,
} from '../forsikring-objekter-checkbox/forsikring-objekter-checkbox-types';
import {
    BesvartForsikringsporsmal,
    CurrentForsikringsporsmal,
    isBesvartForsikringSporsmal,
    isForsikringIngenAlternativerDataType,
    isForsikringListe,
    toCurrentForsikringSporsmal,
} from '../forsikring/forsikring-types';
import {
    BesvartForsikringsnummerSporsmal,
    CurrentForsikringsnummerSporsmal,
} from '../forsikringsnummer/forsikringsnummer-types';
import { BesvartInformasjonsporsmal, CurrentInformasjonsporsmal } from '../informasjon/informasjon-types';
import {
    BesvartKontaktInfoInvolvertSporsmal,
    CurrentKontaktInfoInvolvertSporsmal,
    isKontaktInfoInvolvertValid,
} from '../kontaktInfoInvolvert/kontaktInfoInvolvert-types';
import {
    BesvartKontaktinfosporsmal,
    CurrentKontaktinfosporsmal,
    initializeCurrentKontaktinfosporsmal,
} from '../kontaktinfo/kontaktinfo-types';
import { isKontaktinfoValid, removeSpaces } from '../kontaktinfo/kontaktinfofelt';
import {
    BesvartKredittkortnummerSporsmal,
    CurrentKredittkortnummerSporsmal,
    validatePartialKredittkortnummer,
} from '../kredittkortnummer/kredittkortnummer-types';
import { BesvartModalSporsmal, CurrentModalSporsmal } from '../modal/modal-types';
import {
    BesvartPostnummersporsmal,
    CurrentPostnummersporsmal,
    isBesvartPostnummerSporsmal,
} from '../postnummer/postnummer-types';
import { BesvartRadiosporsmal, CurrentRadiosporsmal } from '../radio/radio-types';
import { BesvartSelectSporsmal, CurrentSelectSporsmal } from '../select/select-types';
import {
    BesvartSimpleKontaktinfosporsmal,
    CurrentSimpleKontaktinfosporsmal,
} from '../simpleKontaktinfo/simpleKontaktinfo-types';
import { isSimpleKontaktinfoValid } from '../simpleKontaktinfo/simpleKontaktinfofelt';
import {
    BesvartSkadedatoAndForsikringSporsmal,
    CurrentSkadedatoAndForsikringSporsmal,
    isBesvartSkadedatoAndForsikringSporsmal,
    isForsikringIngenAlternativerType,
    isForsikringListeType,
    toCurrentSkadedatoAndForsikringSporsmal,
} from '../skadedato-and-forsikring/skadedato-and-forsikring-types';
import {
    BesvartSkadelidtInfoSporsmal,
    CurrentSkadelidtInfoSporsmal,
    isSkadelidtInfoSporsmalValid,
} from '../skadelidtInfo/skadelidtInfo-types';
import {
    BesvartSkadenummerSporsmal,
    CurrentSkadenummerSporsmal,
    erGyldigSkadenummer,
} from '../skadenummer/skadenummer-types';
import {
    BesvartSporsmalList,
    copyNextSvarToCurrentSporsmalList,
    CurrentSporsmalList,
    initializeCurrentSporsmalList,
    sporsmalListValid,
    toBesvartSporsmalList,
} from '../sporsmalList/sporsmalList-types';
import {
    BesvartSporsmalSide,
    copyNextSvarToCurrentSporsmalSide,
    CurrentSporsmalSide,
    getVisibleSporsmal,
    initializeCurrentSporsmalSide,
    toBesvartSporsmalSide,
} from '../sporsmalSide/sporsmalSide-types';
import { BesvartTallsporsmal, CurrentTallsporsmal, getTallNumber } from '../tall/tall-types';
import { isValidTallHalv, isValidTallHel } from '../tall/tall-validation';
import { BesvartTekstsporsmal, CurrentTekstsporsmal } from '../tekst/tekst-types';
import { isValidUtleggslinjer } from '../utlegg/utlegg';
import {
    BesvartUtleggsporsmal,
    CurrentUtleggsporsmal,
    emptyUtleggslinje,
    utleggslinjeContainsInformation,
} from '../utlegg/utlegg-types';
import { CurrentUtsattForgreiningsporsmal } from '../utsattForgreining/utsatt-forgreining-types';
import {
    isInputForSmallTextInputSizesValid,
    validateEpost,
    validateFodselsnummer,
    validateForsikringnummer,
    validateMobilnummer,
    validatePostnummer,
    validateSkadedatoAndForsikringSporsmal,
    validateTelefonnummer,
} from '../validation/validate';
import {
    BesvartVerkstedsporsmal,
    CurrentVerkstedsporsmal,
    isBesvartVerkstedSporsmal,
    isValidVerkstedSporsmal,
    Verksted,
} from '../verksted/verksted-types';
import { IdentifikatorType, RadioAlternativ, Sporsmal, toUtleggslinjeDTO } from './sporsmalsflyt-types';

export enum CurrentStegTypes {
    Loading = 'Loading',
    Failure = 'Failure',
    Sporsmal = 'Sporsmal',
    Oppsummering = 'Oppsummering',
}

interface LoadingSporsmalSteg {
    type: CurrentStegTypes.Loading;
}

interface FailureFetchingSporsmal {
    type: CurrentStegTypes.Failure;
}

export interface SporsmalSteg {
    type: CurrentStegTypes.Sporsmal;
    currentSporsmal: CurrentSporsmal;
}

export interface OppsummeringSteg {
    type: CurrentStegTypes.Oppsummering;
}

export const loadingSporsmalSteg = (): LoadingSporsmalSteg => ({
    type: CurrentStegTypes.Loading,
});
export const failureFetchingSporsmal = (): FailureFetchingSporsmal => ({
    type: CurrentStegTypes.Failure,
});

export const currentSporsmalSteg = (currentSporsmal: CurrentSporsmal): SporsmalSteg => ({
    type: CurrentStegTypes.Sporsmal,
    currentSporsmal,
});

export const oppsummeringSteg = (): OppsummeringSteg => ({
    type: CurrentStegTypes.Oppsummering,
});

export const isSporsmalSteg = (currentSteg: CurrentSteg): currentSteg is SporsmalSteg =>
    currentSteg.type === CurrentStegTypes.Sporsmal;

export type CurrentSteg = LoadingSporsmalSteg | FailureFetchingSporsmal | SporsmalSteg | OppsummeringSteg;

export type BesvartSporsmal =
    | BesvartRadiosporsmal
    | BesvartCheckboxSporsmal
    | BesvartForsikringsporsmal
    | BesvartSkadedatoAndForsikringSporsmal
    | BesvartTekstsporsmal
    | BesvartDatosporsmal
    | BesvartTallsporsmal
    | BesvartKontaktinfosporsmal
    | BesvartKontaktInfoInvolvertSporsmal
    | BesvartSimpleKontaktinfosporsmal
    | BesvartSkadelidtInfoSporsmal
    | BesvartFilopplastingsporsmal
    | BesvartUtleggsporsmal
    | BesvartVerkstedsporsmal
    | BesvartInformasjonsporsmal
    | BesvartSporsmalSide
    | BesvartSporsmalList
    | BesvartSkadenummerSporsmal
    | BesvartAttentionMessageSporsmal
    | BesvartPostnummersporsmal
    | BesvartSelectSporsmal
    | BesvartForsikringsnummerSporsmal
    | BesvartForsikringObjekterCheckboxSporsmal
    | BesvartKredittkortnummerSporsmal
    | BesvartCardSporsmal
    | BesvartModalSporsmal;

export interface CurrentUtleggslinje {
    beskrivelse: string;
    dato?: DateAsString;
    utgift: string;
    refundert: string;
    dagensPris: string;
    id: number;
    utleggType: UtleggType;
}

export type CurrentSporsmal =
    | CurrentRadiosporsmal
    | CurrentCheckboxSporsmal
    | CurrentForsikringsporsmal
    | CurrentSkadedatoAndForsikringSporsmal
    | CurrentTekstsporsmal
    | CurrentDatosporsmal
    | CurrentTallsporsmal
    | CurrentKontaktinfosporsmal
    | CurrentKontaktInfoInvolvertSporsmal
    | CurrentSimpleKontaktinfosporsmal
    | CurrentSkadelidtInfoSporsmal
    | CurrentFilopplastingsporsmal
    | CurrentUtleggsporsmal
    | CurrentVerkstedsporsmal
    | CurrentInformasjonsporsmal
    | CurrentBlindveisporsmal
    | CurrentUtsattForgreiningsporsmal
    | CurrentSporsmalSide
    | CurrentSporsmalList
    | CurrentSkadenummerSporsmal
    | CurrentAttentionMessageSporsmal
    | CurrentCardSporsmal
    | CurrentPostnummersporsmal
    | CurrentSelectSporsmal
    | CurrentForsikringsnummerSporsmal
    | CurrentForsikringObjekterCheckboxSporsmal
    | CurrentKredittkortnummerSporsmal
    | CurrentModalSporsmal;

const isBesvartSkadenummerSporsmal = (sporsmal: CurrentSkadenummerSporsmal): sporsmal is BesvartSkadenummerSporsmal =>
    sporsmal.skadenummer && sporsmal.skadenummer.length >= 1 ? erGyldigSkadenummer(sporsmal) : false;

const verkstedToDTO = (verksted: Verksted): VerkstedWebDTO => ({
    adresse: verksted.adresse,
    id: verksted.id,
    navn: verksted.navn,
    postnummer: verksted.postnummer,
    poststed: verksted.poststed,
    telefonnummer: verksted.telefonnummer,
});

export const removeSporsmalWithoutSvarFilter = (besvarteSporsmal: BesvartSporsmal[]): BesvartSporsmal[] =>
    besvarteSporsmal
        .filter((besvartSporsmal) => besvartSporsmal.type !== SporsmalType.Filopplasting)
        .filter((besvartSporsmal) => besvartSporsmal.type !== SporsmalType.Card);

const besvartSporsmalToSvarDTO = (besvartSporsmal: BesvartSporsmal): SvarDTOUnion | SvarDTOUnion[] => {
    switch (besvartSporsmal.type) {
        case SporsmalType.Radio:
            return {
                sporsmalId: besvartSporsmal.sporsmal.id,
                sporsmalType: SporsmalType.Radio,
                type: SporsmalType.Radio,
                svarId: besvartSporsmal.svarId,
            };
        case SporsmalType.Select:
            return {
                sporsmalId: besvartSporsmal.sporsmal.id,
                sporsmalType: SporsmalType.Select,
                type: SporsmalType.Select,
                svarId: besvartSporsmal.svarId,
            };
        case SporsmalType.Checkbox:
            return {
                sporsmalId: besvartSporsmal.sporsmal.id,
                sporsmalType: SporsmalType.Checkbox,
                type: SporsmalType.Checkbox,
                valgteAlternativer: besvartSporsmal.alternativer
                    .filter((alternativ) => alternativ.checked)
                    .map((alternativ) => alternativ.id),
            };
        case SporsmalType.Forsikring:
            if (isForsikringIngenAlternativerDataType(besvartSporsmal.data)) {
                return {
                    sporsmalId: besvartSporsmal.sporsmal.id,
                    sporsmalType: SporsmalType.Forsikring,
                    type: SporsmalType.Forsikring,
                    svar: ForsikringDekningDTO.FinnerIkkeRiktig,
                    forsikringsnumerFraAlternativ: null,
                    forsikringsnummerFantIkkeRiktig: besvartSporsmal.data.forsikringsnummer,
                };
            }
            return {
                sporsmalId: besvartSporsmal.sporsmal.id,
                sporsmalType: SporsmalType.Forsikring,
                type: SporsmalType.Forsikring,
                svar: besvartSporsmal.data.selectedAlternativ.id,
                forsikringstekst: besvartSporsmal.data.selectedAlternativ.tekst,
                undertekst: besvartSporsmal.data.selectedAlternativ.undertekst,
                forsikringsnumerFraAlternativ: besvartSporsmal.data.selectedAlternativ.forsikringsnummer,
            };
        case SporsmalType.SkadedatoAndForsikring:
            if (isForsikringIngenAlternativerType(besvartSporsmal.forsikringData)) {
                return {
                    type: SporsmalType.SkadedatoAndForsikring,
                    sporsmalType: SporsmalType.SkadedatoAndForsikring,
                    sporsmalId: besvartSporsmal.sporsmal.id,
                    skadedatoSvar: new Date(besvartSporsmal.skadedato).toISOString(),
                    forsikringDekningSvar: ForsikringDekningDTO.IngenValgt,
                    forsikringsnummerFraAlternativ: null,
                };
            }
            return {
                type: SporsmalType.SkadedatoAndForsikring,
                sporsmalType: SporsmalType.SkadedatoAndForsikring,
                sporsmalId: besvartSporsmal.sporsmal.id,
                skadedatoSvar: new Date(besvartSporsmal.skadedato).toISOString(),
                forsikringDekningSvar: besvartSporsmal.forsikringData.selectedAlternativ.id,
                forsikringstekst: besvartSporsmal.forsikringData.selectedAlternativ.tekst,
                forsikringsnummerFraAlternativ: besvartSporsmal.forsikringData.selectedAlternativ.forsikringsnummer,
            };
        case SporsmalType.Forsikringsnummer:
            return {
                type: SporsmalType.Forsikringsnummer,
                sporsmalType: SporsmalType.Forsikringsnummer,
                sporsmalId: besvartSporsmal.sporsmal.id,
                forsikringsnummer: besvartSporsmal.forsikringsnummer,
            };
        case SporsmalType.Tekst:
            return {
                sporsmalId: besvartSporsmal.sporsmal.id,
                sporsmalType: SporsmalType.Tekst,
                type: SporsmalType.Tekst,
                svarTekst: besvartSporsmal.svarTekst,
            };
        case SporsmalType.Dato:
            return {
                sporsmalId: besvartSporsmal.sporsmal.id,
                sporsmalType: SporsmalType.Dato,
                type: SporsmalType.Dato,
                svarTekst: new Date(besvartSporsmal.dato).toISOString(),
            };
        case SporsmalType.Tall:
            return {
                sporsmalId: besvartSporsmal.sporsmal.id,
                sporsmalType: SporsmalType.Tall,
                type: SporsmalType.Tall,
                svarTall: getTallNumber(besvartSporsmal),
            };
        case SporsmalType.Kontaktinfo:
            return {
                sporsmalId: besvartSporsmal.sporsmal.id,
                sporsmalType: SporsmalType.Kontaktinfo,
                type: SporsmalType.Kontaktinfo,
                bankkontonummer: besvartSporsmal.bankkontonummer,
                epost: besvartSporsmal.epost,
                identifikator:
                    besvartSporsmal.identifikatorType === IdentifikatorType.Fodselsnummer
                        ? besvartSporsmal.fodselsnummer
                        : removeSpaces(besvartSporsmal.organisasjonsnummer),
                identifikatorType: besvartSporsmal.identifikatorType,
                mobilnummer: besvartSporsmal.mobilnummer,
                navn: besvartSporsmal.navn,
                showFodselsnummer: besvartSporsmal.showFodselsnummer,
            };
        case SporsmalType.KontaktInfoInvolvert:
            return {
                sporsmalId: besvartSporsmal.sporsmal.id,
                sporsmalType: SporsmalType.KontaktInfoInvolvert,
                type: SporsmalType.KontaktInfoInvolvert,
                navn: besvartSporsmal.navn,
                mobilnummer: besvartSporsmal.mobilnummer,
                adresse: besvartSporsmal.adresse,
                fodselsdato: besvartSporsmal.fodselsdato,
            };
        case SporsmalType.SimpleKontaktinfo:
            return {
                sporsmalId: besvartSporsmal.sporsmal.id,
                sporsmalType: SporsmalType.SimpleKontaktinfo,
                type: SporsmalType.SimpleKontaktinfo,
                fulltNavn: besvartSporsmal.fulltNavn,
                mobilnummer: besvartSporsmal.mobilnummer,
                identifikator: besvartSporsmal.fodselsnummer,
            };
        case SporsmalType.SkadelidtInfo:
            return {
                sporsmalId: besvartSporsmal.sporsmal.id,
                sporsmalType: SporsmalType.SkadelidtInfo,
                type: SporsmalType.SkadelidtInfo,
                fulltNavn: besvartSporsmal.fulltNavn,
                fodselsnummer: besvartSporsmal.fodselsnummer,
                fastlegeKontor: besvartSporsmal.fastlegeKontor,
                hasBankIdOgMobil: besvartSporsmal.hasBankIdOgMobil,
                mobilnummer: besvartSporsmal.mobilnummer,
            };
        case SporsmalType.Skadenummer:
            if (isBesvartSkadenummerSporsmal(besvartSporsmal)) {
                return {
                    sporsmalId: besvartSporsmal.sporsmal.id,
                    sporsmalType: SporsmalType.Skadenummer,
                    type: SporsmalType.Skadenummer,
                    skadenummer: besvartSporsmal.skadenummer,
                };
            }
            return undefined;
        case SporsmalType.Filopplasting:
            return undefined;
        case SporsmalType.Card:
            return undefined;
        case SporsmalType.Utlegg:
            return {
                sporsmalId: besvartSporsmal.sporsmal.id,
                sporsmalType: SporsmalType.Utlegg,
                type: SporsmalType.Utlegg,
                utleggslinjer: besvartSporsmal.utleggslinjer
                    .filter(utleggslinjeContainsInformation)
                    .map(toUtleggslinjeDTO),
            };
        case SporsmalType.Verksted:
            return {
                sporsmalId: besvartSporsmal.sporsmal.id,
                sporsmalType: SporsmalType.Verksted,
                bilmerke: besvartSporsmal.bilmerke,
                postnummer: { value: besvartSporsmal.postnummer },
                type: SporsmalType.Verksted,
                verksteder:
                    besvartSporsmal.verksteder.type === RemoteDataType.SUCCESS
                        ? besvartSporsmal.verksteder.data.map((verksted) => verkstedToDTO(verksted))
                        : [],
            };
        case SporsmalType.Informasjon:
            return {
                sporsmalId: besvartSporsmal.sporsmal.id,
                sporsmalType: SporsmalType.Informasjon,
                type: SporsmalType.Informasjon,
            };
        case SporsmalType.SporsmalSide:
            return removeSporsmalWithoutSvarFilter(besvartSporsmal.besvarteSporsmal)
                .map(besvartSporsmalToSvarDTO)
                .flat();
        case SporsmalType.SporsmalList:
            return {
                sporsmalId: besvartSporsmal.sporsmal.id,
                sporsmalType: SporsmalType.SporsmalList,
                type: SporsmalType.SporsmalList,
                svarCollection: besvartSporsmal.besvarteSporsmal.map((b) => ({
                    svar: b.map(besvartSporsmalToSvarDTO).flat(),
                })),
            };
        case SporsmalType.AttentionMessage:
            return {
                sporsmalId: besvartSporsmal.sporsmal.id,
                sporsmalType: SporsmalType.AttentionMessage,
                type: SporsmalType.AttentionMessage,
            };
        case SporsmalType.Postnummer:
            return {
                sporsmalId: besvartSporsmal.sporsmal.id,
                sporsmalType: SporsmalType.Postnummer,
                type: SporsmalType.Postnummer,
                postnummer: besvartSporsmal.postnummer,
                poststed: besvartSporsmal.poststed.type === RemoteDataType.SUCCESS ? besvartSporsmal.poststed.data : '',
            };
        case SporsmalType.Kredittkortnummer:
            return {
                type: SporsmalType.Kredittkortnummer,
                sporsmalId: besvartSporsmal.sporsmal.id,
                sporsmalType: SporsmalType.Kredittkortnummer,
                kredittkortnummer: besvartSporsmal.kredittkortnummer,
            };
        case SporsmalType.ForsikringObjekterCheckbox:
            return {
                sporsmalId: besvartSporsmal.sporsmal.id,
                sporsmalType: SporsmalType.ForsikringObjekterCheckbox,
                type: SporsmalType.ForsikringObjekterCheckbox,
                valgteObjekter: besvartSporsmal.objekter
                    .filter((objekt) => objekt.checked)
                    .map((objekt) => ({
                        objektnavn: objekt.objektnavn,
                        objektnummer: objekt.objektnummer,
                        type: 'ValgtForsikretObjektDTO',
                    })),
            };
        case SporsmalType.Modal:
            return {
                sporsmalId: besvartSporsmal.sporsmal.id,
                sporsmalType: SporsmalType.Modal,
                type: SporsmalType.Modal,
            };
        default:
            return assertUnreachable(besvartSporsmal);
    }
};

export const toCurrentSporsmal = (
    sporsmal: Sporsmal,
    besvarteSporsmal: BesvartSporsmal[] = [],
    kundeinfo: Kundeinfo = undefined,
    identifikatorType: IdentifikatorType = IdentifikatorType.Fodselsnummer,
    skadeDato: DateAsString | undefined = undefined,
): CurrentSporsmal | undefined => {
    switch (sporsmal.type) {
        case SporsmalType.Radio:
            return { type: SporsmalType.Radio, sporsmal };
        case SporsmalType.Select:
            return { type: SporsmalType.Select, sporsmal };
        case SporsmalType.Checkbox:
            return {
                type: SporsmalType.Checkbox,
                sporsmal,
                alternativer: sporsmal.alternativer.map((alternativ) => ({ ...alternativ, checked: false })),
            };
        case SporsmalType.Forsikring:
            return toCurrentForsikringSporsmal(sporsmal, skadeDato, kundeinfo);
        case SporsmalType.SkadedatoAndForsikring:
            return toCurrentSkadedatoAndForsikringSporsmal(sporsmal, kundeinfo, skadeDato);
        case SporsmalType.Forsikringsnummer:
            return { type: SporsmalType.Forsikringsnummer, sporsmal };
        case SporsmalType.Tekst:
            return { type: SporsmalType.Tekst, sporsmal, svarTekst: '' };
        case SporsmalType.Dato:
            return { type: SporsmalType.Dato, sporsmal };
        case SporsmalType.Tall:
            return {
                type: SporsmalType.Tall,
                sporsmal,
                tall: undefined,
                maleenhet: sporsmal.maleenhet ? sporsmal.maleenhet : '',
                tallvalidering: sporsmal.tallvalidering,
            };
        case SporsmalType.Filopplasting:
            return { type: SporsmalType.Filopplasting, sporsmal, filer: [] };
        case SporsmalType.Kontaktinfo:
            return initializeCurrentKontaktinfosporsmal(sporsmal, kundeinfo, identifikatorType, besvarteSporsmal);
        case SporsmalType.KontaktInfoInvolvert:
            return {
                type: SporsmalType.KontaktInfoInvolvert,
                sporsmal,
                navn: '',
                mobilnummer: '',
                adresse: '',
                fodselsdato: '',
            };
        case SporsmalType.SimpleKontaktinfo:
            return {
                type: SporsmalType.SimpleKontaktinfo,
                sporsmal,
                fulltNavn: '',
                mobilnummer: '',
                fodselsnummer: '',
            };
        case SporsmalType.SkadelidtInfo:
            return {
                type: SporsmalType.SkadelidtInfo,
                sporsmal,
                fulltNavn: '',
                fodselsnummer: '',
                fastlegeKontor: '',
                hasBankIdOgMobil: null,
                mobilnummer: '',
            };
        case SporsmalType.Utlegg:
            return {
                type: SporsmalType.Utlegg,
                sporsmal,
                utleggslinjer: [emptyUtleggslinje(sporsmal.utleggType)],
            };
        case SporsmalType.Skadenummer:
            return { type: SporsmalType.Skadenummer, sporsmal, skadenummer: undefined };
        case SporsmalType.Verksted:
            return {
                type: SporsmalType.Verksted,
                id: '',
                sporsmal,
                postnummer: '',
                bilmerke: undefined,
                verksteder: notAsked(),
            };
        case SporsmalType.Informasjon:
            return { type: SporsmalType.Informasjon, sporsmal };
        case SporsmalType.Blindvei:
            return { type: SporsmalType.Blindvei, sporsmal };
        case SporsmalType.UtsattForgreining:
            return { type: SporsmalType.UtsattForgreining, sporsmal };
        case SporsmalType.AttentionMessage:
            return { type: SporsmalType.AttentionMessage, sporsmal };
        case SporsmalType.Card:
            return { type: SporsmalType.Card, sporsmal };
        case SporsmalType.Postnummer:
            return { type: SporsmalType.Postnummer, sporsmal, postnummer: '', poststed: notAsked() };
        case SporsmalType.SporsmalSide:
            return initializeCurrentSporsmalSide(sporsmal, besvarteSporsmal, kundeinfo);
        case SporsmalType.SporsmalList:
            return initializeCurrentSporsmalList(sporsmal, besvarteSporsmal);
        case SporsmalType.ForsikringObjekterCheckbox:
            return {
                type: SporsmalType.ForsikringObjekterCheckbox,
                sporsmal,
                objekter: getValgteObjekterFromBesvartSporsmalOrEmpty(sporsmal.id, besvarteSporsmal),
            };
        case SporsmalType.Kredittkortnummer:
            return {
                type: SporsmalType.Kredittkortnummer,
                sporsmal,
                kredittkortnummer: '',
            };
        case SporsmalType.Modal:
            return {
                type: SporsmalType.Modal,
                sporsmal,
            };
        default:
            return assertUnreachable(sporsmal);
    }
};

export const flattenBesvarteSporsmal = (besvarteSporsmal: BesvartSporsmal[]): BesvartSporsmal[] =>
    besvarteSporsmal.flatMap((besvartSporsmal) => {
        if (besvartSporsmal.type === SporsmalType.SporsmalSide) {
            return flattenBesvarteSporsmal(besvartSporsmal.besvarteSporsmal);
        }
        return [besvartSporsmal];
    });

export const besvarteSporsmalToSvarDTO = (besvarteSporsmal: BesvartSporsmal[]): SvarDTOUnion[] =>
    removeSporsmalWithoutSvarFilter(besvarteSporsmal).map(besvartSporsmalToSvarDTO).flat();

export const isCurrentSporsmalValid = (currentSporsmal: CurrentSporsmal): boolean => {
    switch (currentSporsmal.type) {
        case SporsmalType.Tekst:
            if (
                currentSporsmal.sporsmal.maxNumberOfCharacters &&
                currentSporsmal.svarTekst.length > currentSporsmal.sporsmal.maxNumberOfCharacters
            )
                return false;
            switch (currentSporsmal.sporsmal.valideringstype) {
                case Valideringstype.Kontonummer:
                    return erGyldigKontonummer(currentSporsmal.svarTekst);
                case Valideringstype.Epost:
                    return validateEpost(currentSporsmal.svarTekst);
                case Valideringstype.Pakrevd:
                    switch (currentSporsmal.sporsmal.size) {
                        case TextInputSize.ExtraSmall:
                        case TextInputSize.Smaller:
                        case TextInputSize.Small:
                            return isInputForSmallTextInputSizesValid(currentSporsmal.svarTekst);
                        case TextInputSize.Large:
                        default:
                            return currentSporsmal.svarTekst !== '';
                    }
                case Valideringstype.Fodselsnummer:
                    return validateFodselsnummer(currentSporsmal.svarTekst);
                case Valideringstype.Mobilnummer:
                    return validateMobilnummer(currentSporsmal.svarTekst);
                case Valideringstype.ValgfrittMobilnummer:
                    return !currentSporsmal.svarTekst ? true : validateMobilnummer(currentSporsmal.svarTekst);
                case Valideringstype.ValgfrittTelefonnummer:
                    return !currentSporsmal.svarTekst ? true : validateTelefonnummer(currentSporsmal.svarTekst);
                case Valideringstype.Postnummer:
                    return validatePostnummer(currentSporsmal.svarTekst);
                case Valideringstype.Valgfritt:
                case Valideringstype.Skadenummer:
                    return true;
                default:
                    return assertUnreachable(currentSporsmal.sporsmal.valideringstype);
            }
        case SporsmalType.Dato:
            return !!currentSporsmal.dato;

        case SporsmalType.Kontaktinfo:
            return isKontaktinfoValid(currentSporsmal);

        case SporsmalType.KontaktInfoInvolvert:
            return isKontaktInfoInvolvertValid(currentSporsmal);

        case SporsmalType.SimpleKontaktinfo:
            return isSimpleKontaktinfoValid(currentSporsmal);

        case SporsmalType.SkadelidtInfo:
            return isSkadelidtInfoSporsmalValid(currentSporsmal);

        case SporsmalType.Radio:
            return (
                currentSporsmal.svarId &&
                currentSporsmal.sporsmal.alternativer.some(
                    (alternativ: RadioAlternativ) =>
                        alternativ.id === currentSporsmal.svarId && !alternativ.feilmelding,
                )
            );

        case SporsmalType.Checkbox:
            return currentSporsmal.alternativer.filter((alternativ) => alternativ.checked).length > 0;

        case SporsmalType.Filopplasting:
            if (currentSporsmal.sporsmal.valideringstype === Valideringstype.Pakrevd) {
                return currentSporsmal.filer.length > 0;
            }
            return true;

        case SporsmalType.Utlegg:
            return isValidUtleggslinjer(currentSporsmal.utleggslinjer, currentSporsmal.sporsmal.toggles.includeDato);

        case SporsmalType.Verksted:
            return isValidVerkstedSporsmal(currentSporsmal);

        case SporsmalType.Tall:
            if (currentSporsmal.tall === undefined) {
                return false;
            }
            if (currentSporsmal.tallvalidering === TallValidationType.Hel) {
                return isValidTallHel(currentSporsmal.tall);
            }
            if (currentSporsmal.tallvalidering === TallValidationType.Halv) {
                return isValidTallHalv(currentSporsmal.tall);
            }
            return currentSporsmal.tall >= 0;

        case SporsmalType.SporsmalSide:
            return getVisibleSporsmal(currentSporsmal).every((c) => isCurrentSporsmalValid(c));

        case SporsmalType.SporsmalList:
            return sporsmalListValid(currentSporsmal.sporsmalListSporsmalsflytCollection);

        case SporsmalType.Skadenummer:
            return currentSporsmal.skadenummer && currentSporsmal.skadenummer.length >= 1
                ? erGyldigSkadenummer(currentSporsmal)
                : false;

        case SporsmalType.Postnummer:
            return validatePostnummer(currentSporsmal.postnummer);

        case SporsmalType.Select:
            return currentSporsmal.svarId !== undefined;

        case SporsmalType.SkadedatoAndForsikring:
            return validateSkadedatoAndForsikringSporsmal(currentSporsmal.skadedato, currentSporsmal.forsikringData);

        case SporsmalType.Forsikringsnummer:
            return validateForsikringnummer(currentSporsmal.forsikringsnummer);

        case SporsmalType.ForsikringObjekterCheckbox:
            return validateForsikringObjekterCheckbox(currentSporsmal.objekter);

        case SporsmalType.Kredittkortnummer:
            return validatePartialKredittkortnummer(currentSporsmal.kredittkortnummer);

        case SporsmalType.Informasjon:
        case SporsmalType.Forsikring:
        case SporsmalType.Blindvei:
        case SporsmalType.UtsattForgreining:
        case SporsmalType.AttentionMessage:
        case SporsmalType.Card:
        case SporsmalType.Modal:
            return true;
        default:
            return assertUnreachable(currentSporsmal);
    }
};

const isBesvartRadioSporsmal = (sporsmal: CurrentRadiosporsmal): sporsmal is BesvartRadiosporsmal =>
    sporsmal.sporsmal.alternativer.find((alternativ) => alternativ.id === sporsmal.svarId && !alternativ.feilmelding) &&
    sporsmal.svarId !== undefined;

const isBesvartSelectSporsmal = (sporsmal: CurrentSelectSporsmal): sporsmal is BesvartSelectSporsmal =>
    sporsmal.sporsmal.alternativer.find((alternativ) => alternativ.id === sporsmal.svarId) &&
    sporsmal.svarId !== undefined;

const isBesvartForsikringsnummerSporsmal = (
    sporsmal: CurrentForsikringsnummerSporsmal,
): sporsmal is BesvartForsikringsnummerSporsmal => sporsmal.forsikringsnummer !== undefined;

export const toBesvartSporsmal = (currentSporsmal: CurrentSporsmal): BesvartSporsmal | undefined => {
    switch (currentSporsmal.type) {
        case SporsmalType.Radio:
            if (isBesvartRadioSporsmal(currentSporsmal)) {
                return currentSporsmal;
            }
            return undefined;

        case SporsmalType.Select:
            if (isBesvartSelectSporsmal(currentSporsmal)) {
                return currentSporsmal;
            }
            return undefined;
        case SporsmalType.Checkbox:
            if (isBesvartCheckboxSporsmal(currentSporsmal)) {
                return {
                    ...currentSporsmal,
                    alternativer: currentSporsmal.alternativer,
                };
            }
            return undefined;
        case SporsmalType.Forsikring:
            if (isBesvartForsikringSporsmal(currentSporsmal)) {
                return currentSporsmal;
            }
            return undefined;
        case SporsmalType.SkadedatoAndForsikring:
            if (isBesvartSkadedatoAndForsikringSporsmal(currentSporsmal)) return currentSporsmal;
            return undefined;

        case SporsmalType.Forsikringsnummer:
            if (isBesvartForsikringsnummerSporsmal(currentSporsmal)) return currentSporsmal;
            return undefined;

        case SporsmalType.Dato:
            if (isBesvartDatosporsmal(currentSporsmal)) {
                return currentSporsmal;
            }
            return undefined;

        case SporsmalType.Verksted:
            if (isBesvartVerkstedSporsmal(currentSporsmal)) {
                return currentSporsmal;
            }
            return undefined;
        case SporsmalType.Tall:
            return {
                ...currentSporsmal,
                tall: currentSporsmal.tall || 0,
            };
        case SporsmalType.SporsmalSide:
            return toBesvartSporsmalSide(currentSporsmal);
        case SporsmalType.SporsmalList:
            return toBesvartSporsmalList(currentSporsmal);
        case SporsmalType.Blindvei:
            return undefined;
        case SporsmalType.UtsattForgreining:
            return undefined;
        case SporsmalType.SimpleKontaktinfo:
            return currentSporsmal;
        case SporsmalType.Skadenummer:
            if (isBesvartSkadenummerSporsmal(currentSporsmal)) {
                return currentSporsmal;
            }
            return undefined;
        case SporsmalType.Filopplasting:
            if (isFilopplastingsporsmal(currentSporsmal)) {
                return {
                    ...currentSporsmal,
                    ids: currentSporsmal.filer
                        .filter((f) => f.status === VedleggStatus.Ok)
                        .map((f) => {
                            return {
                                id: f.id,
                                uploadId: f.uploadId,
                                fileName: f.fileName,
                            };
                        }),
                };
            }
            return undefined;
        case SporsmalType.Postnummer: {
            if (isBesvartPostnummerSporsmal(currentSporsmal)) {
                return currentSporsmal;
            }
            return undefined;
        }
        case SporsmalType.ForsikringObjekterCheckbox:
            if (isBesvartForsikringObjekterCheckboxSporsmal(currentSporsmal)) {
                return {
                    ...currentSporsmal,
                    objekter: currentSporsmal.objekter,
                };
            }
            return undefined;

        case SporsmalType.Kredittkortnummer:
        case SporsmalType.Informasjon:
        case SporsmalType.Tekst:
        case SporsmalType.Kontaktinfo:
        case SporsmalType.KontaktInfoInvolvert:
        case SporsmalType.SkadelidtInfo:
        case SporsmalType.Utlegg:
        case SporsmalType.AttentionMessage:
        case SporsmalType.Card:
        case SporsmalType.Modal:
            return currentSporsmal;
        default:
            return assertUnreachable(currentSporsmal);
    }
};

export const copyNextSvarToCurrentSporsmal = (
    currentSporsmal: CurrentSporsmal,
    next: CurrentSporsmal,
    besvarteSporsmal: BesvartSporsmal[],
): CurrentSporsmal | undefined => {
    switch (currentSporsmal.type) {
        case SporsmalType.Radio:
            if (next.type === SporsmalType.Radio) {
                currentSporsmal.svarId = next.svarId;
            }
            return currentSporsmal;

        case SporsmalType.Select:
            if (next.type === SporsmalType.Select) {
                currentSporsmal.svarId = next.svarId;
            }
            return currentSporsmal;
        case SporsmalType.Checkbox:
            if (next.type === SporsmalType.Checkbox) {
                currentSporsmal.alternativer = next.alternativer;
            }
            return currentSporsmal;
        case SporsmalType.Forsikring:
            if (
                next.type === SporsmalType.Forsikring &&
                isForsikringListe(next.data) &&
                isForsikringListe(currentSporsmal.data)
            ) {
                currentSporsmal.data.selectedAlternativ = next.data.selectedAlternativ;
            }
            if (
                next.type === SporsmalType.Forsikring &&
                isForsikringIngenAlternativerDataType(next.data) &&
                isForsikringIngenAlternativerDataType(currentSporsmal.data)
            ) {
                currentSporsmal.data.forsikringsnummer = next.data.forsikringsnummer;
            }
            return currentSporsmal;
        case SporsmalType.SkadedatoAndForsikring:
            if (next.type === SporsmalType.SkadedatoAndForsikring && isForsikringListeType(next.forsikringData)) {
                currentSporsmal.forsikringData = next.forsikringData;
                currentSporsmal.skadedato = next.skadedato;
            }
            if (
                next.type === SporsmalType.SkadedatoAndForsikring &&
                isForsikringIngenAlternativerType(next.forsikringData)
            ) {
                currentSporsmal.forsikringData = next.forsikringData;
                currentSporsmal.skadedato = next.skadedato;
            }
            return currentSporsmal;
        case SporsmalType.Dato:
            if (next.type === SporsmalType.Dato) {
                currentSporsmal.dato = next.dato;
            }
            return currentSporsmal;
        case SporsmalType.Tall:
            if (next.type === SporsmalType.Tall) {
                currentSporsmal.tall = next.tall;
            }
            return {
                ...currentSporsmal,
                tall: currentSporsmal.tall || 0,
            };

        case SporsmalType.Tekst:
            if (next.type === SporsmalType.Tekst) {
                currentSporsmal.svarTekst = next.svarTekst;
            }
            return currentSporsmal;
        case SporsmalType.Kontaktinfo:
            if (next.type === SporsmalType.Kontaktinfo) {
                return {
                    ...currentSporsmal,
                    navn: next.navn,
                    mobilnummer: next.mobilnummer,
                    epost: next.epost,
                    fodselsnummer: next.fodselsnummer,
                    organisasjonsnummer: next.organisasjonsnummer,
                    bankkontonummer: next.bankkontonummer,
                };
            }
            return currentSporsmal;
        case SporsmalType.SimpleKontaktinfo:
            if (next.type === SporsmalType.SimpleKontaktinfo) {
                return {
                    ...currentSporsmal,
                    fulltNavn: next.fulltNavn,
                    mobilnummer: next.mobilnummer,
                    fodselsnummer: next.fodselsnummer,
                };
            }
            return currentSporsmal;
        case SporsmalType.SkadelidtInfo:
            if (next.type === SporsmalType.SkadelidtInfo) {
                return {
                    ...currentSporsmal,
                    fulltNavn: next.fulltNavn,
                    fodselsnummer: next.fodselsnummer,
                    fastlegeKontor: next.fastlegeKontor,
                    hasBankIdOgMobil: next.hasBankIdOgMobil,
                    mobilnummer: next.mobilnummer,
                };
            }
            return currentSporsmal;

        case SporsmalType.Verksted:
            if (next.type === SporsmalType.Verksted) {
                currentSporsmal.postnummer = next.postnummer;
                currentSporsmal.bilmerke = next.bilmerke;
            }
            return currentSporsmal;

        case SporsmalType.Skadenummer:
            if (next.type === SporsmalType.Skadenummer) {
                currentSporsmal.skadenummer = next.skadenummer;
            }
            return currentSporsmal;

        case SporsmalType.Forsikringsnummer:
            if (next.type === SporsmalType.Forsikringsnummer) {
                currentSporsmal.forsikringsnummer = next.forsikringsnummer;
            }
            return currentSporsmal;

        case SporsmalType.Postnummer:
            if (next.type === SporsmalType.Postnummer) {
                currentSporsmal.postnummer = next.postnummer;
                currentSporsmal.poststed = next.poststed;
            }
            return currentSporsmal;

        case SporsmalType.SporsmalSide:
            if (next.type === SporsmalType.SporsmalSide) {
                return copyNextSvarToCurrentSporsmalSide(currentSporsmal, next, besvarteSporsmal);
            }
            return currentSporsmal;

        case SporsmalType.SporsmalList:
            if (next.type === SporsmalType.SporsmalList) {
                return copyNextSvarToCurrentSporsmalList(currentSporsmal, next);
            }
            return currentSporsmal;

        case SporsmalType.ForsikringObjekterCheckbox:
            if (next.type === SporsmalType.ForsikringObjekterCheckbox) currentSporsmal.objekter = next.objekter;

            return currentSporsmal;

        case SporsmalType.Kredittkortnummer:
            if (next.type === SporsmalType.Kredittkortnummer) {
                currentSporsmal.kredittkortnummer = next.kredittkortnummer;
            }
            return currentSporsmal;

        case SporsmalType.Informasjon:
        case SporsmalType.KontaktInfoInvolvert:
        case SporsmalType.Filopplasting:
        case SporsmalType.Utlegg:
        case SporsmalType.Blindvei:
        case SporsmalType.UtsattForgreining:
        case SporsmalType.AttentionMessage:
        case SporsmalType.Card:
        case SporsmalType.Modal:
            return currentSporsmal;

        default:
            return assertUnreachable(currentSporsmal);
    }
};

export const sporsmalHasCustomHeader = (currentSteg: CurrentSteg): boolean =>
    currentSteg.type === CurrentStegTypes.Sporsmal && currentSteg.currentSporsmal.type === SporsmalType.Informasjon;

export const addSporsmalToBesvarteSporsmal = (
    besvartSporsmal: BesvartSporsmal,
    besvarteSporsmal: BesvartSporsmal[],
): BesvartSporsmal[] => [...besvarteSporsmal, besvartSporsmal];

export const currentSporsmalIsEmpty = (currentSporsmal: CurrentSporsmal): boolean => {
    switch (currentSporsmal.type) {
        case SporsmalType.Radio:
            return !currentSporsmal.svarId;

        case SporsmalType.Forsikring:
            return (
                (isForsikringListe(currentSporsmal.data) && !currentSporsmal.data.selectedAlternativ) ||
                (isForsikringIngenAlternativerDataType(currentSporsmal.data) && !currentSporsmal.data.forsikringsnummer)
            );

        case SporsmalType.SkadedatoAndForsikring:
            return (
                (isForsikringListeType(currentSporsmal.forsikringData) &&
                    !currentSporsmal.forsikringData.selectedAlternativ) ||
                isForsikringIngenAlternativerType(currentSporsmal.forsikringData) ||
                !currentSporsmal.skadedato
            );

        case SporsmalType.Dato:
            return !currentSporsmal.dato;

        case SporsmalType.Tekst:
            return !currentSporsmal.svarTekst;

        case SporsmalType.Filopplasting:
            return currentSporsmal.filer.length === 0;

        case SporsmalType.Kontaktinfo:
            return (
                !currentSporsmal.navn &&
                !currentSporsmal.mobilnummer &&
                !currentSporsmal.epost &&
                !currentSporsmal.fodselsnummer &&
                !currentSporsmal.organisasjonsnummer
            );
        case SporsmalType.Select:
            return !currentSporsmal.svarId;

        case SporsmalType.Forsikringsnummer:
            return !currentSporsmal.forsikringsnummer;
        default:
            return true;
    }
};
